import React from "react"
import { Box } from "@infotech/uikit-grid"
import { PrimaryButton, FlatButton } from "@infotech/uikit-button"
import { reduxForm, Field, formValueSelector, change } from "redux-form"
import FieldSet from "@infotech/uikit-fieldset"
import ColorPicker from "src/components/ColorPicker"
import Select from "@infotech/uikit-select"
import { connect } from "react-redux"
import pick from "lodash/pick"
import get from "lodash/get"
import Switch from "@infotech/uikit-switch"
import Input from "@infotech/uikit-input"
import { hasPermission } from "src/engine/modules/currentUser/selectors"
import {
  EVENT_TYPE_CREATE,
  EVENT_TYPE_UPDATE,
} from "src/constants/knownPermissions"
import LinkContent from "./LinkContent"
import TimeDurationPicker from "./TimeDurationPicker"
import styles from "./styles.css"

const InputForm = ({ input, input: { onChange }, makeShowErrors, ...rest }) => (
  <div className={styles[`input-wrapper`]}>
    <Input
      {...input}
      {...rest}
      onChange={value => {
        onChange(value)
        makeShowErrors(false)
      }}
    />
  </div>
)

const FORM_NAME = `eventTypeForm`
const selector = formValueSelector(FORM_NAME)
const getTestAttribute = name => `planner_event_type_item_${name}`

const ResourceTypeField = props => {
  const {
    input: { value, onChange },
    makeShowErrors,
  } = props
  const resourceValue = value ? value.map(item => item.value || item) : null
  return (
    <Select
      {...props}
      value={resourceValue}
      onChange={value => {
        onChange(value)
        makeShowErrors(false)
      }}
    />
  )
}

const WrappedTimeRange = ({
  caption,
  name,
  error,
  isMinutesDisabled,
  isHoursDisabled,
  isDisabled,
  makeShowErrors,
}) => (
  <Box loose flex column pt="1md">
    <Box loose pb="1md">
      <span className={styles.DurationCaption}>{caption}</span>
    </Box>
    <Field
      component={TimeDurationPicker}
      name={name}
      props={{
        getTestAttribute,
        isMinutesDisabled,
        isHoursDisabled,
        error,
        isDisabled,
        makeShowErrors,
      }}
    />
  </Box>
)

const EventTypeForm = props => {
  const {
    isNew,
    showErrors,
    makeShowErrors,
    backToList,
    disableForm,
    handleSubmit,
    resourceTypeList,
    actionStatus,
    externalLinkTemplate,
    onlyDate,
    minExecutionTime,
    maxExecutionTime,
    defExecutionTime,
    changeFormValue,
    changeDate,
  } = props
  const {
    data: resourceTypeData,
    status: resourceTypeStatus,
  } = resourceTypeList
  const resourceTypeOptions = resourceTypeData.map(item => ({
    value: item.id,
    label: item.name,
  }))
  const validate = get(actionStatus, `validate`, {})

  return (
    <Box loose flex column>
      <form onSubmit={handleSubmit}>
        <Box loose className={styles.EventTypeForm} flex column p="2md">
          <Box loose flex row>
            <Box
              loose
              w="220px"
              pb={showErrors && validate.name ? `0` : `2md`}
              data-testid={getTestAttribute(`name`)}
            >
              <FieldSet caption="Наименование" required>
                <Field
                  name="name"
                  component={InputForm}
                  makeShowErrors={makeShowErrors}
                  placeholder="Введите наименование"
                  error={showErrors && validate.name}
                  disabled={disableForm}
                />
              </FieldSet>
            </Box>
            <Box loose pt="4md" pl="2md">
              <Field component={ColorPicker} name="color" />
            </Box>
          </Box>
          <Box
            loose
            pb={showErrors && validate.allowedResourceTypes ? `0` : `2md`}
            data-testid={getTestAttribute(`allowedResourceTypes`)}
          >
            <FieldSet caption="Тип ресурса" required>
              <Field
                name="allowedResourceTypes"
                component={ResourceTypeField}
                placeholder="Введите тип ресурса"
                makeShowErrors={makeShowErrors}
                options={resourceTypeOptions}
                isMulti
                disabled={resourceTypeStatus.pending || disableForm}
                error={showErrors && validate.allowedResourceTypes}
              />
            </FieldSet>
          </Box>
          <Box loose w="188px" data-testid={getTestAttribute(`code`)}>
            <FieldSet
              caption="Код"
              required
              pb={showErrors && validate.code ? `0` : `2md`}
            >
              <Field
                name="code"
                component={InputForm}
                placeholder="Введите код"
                error={showErrors && validate.code}
                makeShowErrors={makeShowErrors}
                disabled={disableForm}
              />
            </FieldSet>
          </Box>
          <Box loose column pt="2md">
            <Box loose pb="1md">
              <span className={styles.DurationEventType}>
                Длительность события
              </span>
            </Box>
            {[
              {
                caption: `Минимальная`,
                name: `minExecutionTime`,
                isHoursDisabled: onlyDate,
                isMinutesDisabled: onlyDate,
                error: showErrors && validate.minExecutionTime,
                makeShowErrors,
                isDisabled: disableForm,
              },
              {
                caption: `Максимальная`,
                name: `maxExecutionTime`,
                isHoursDisabled: onlyDate,
                isMinutesDisabled: onlyDate,
                error: showErrors && validate.maxExecutionTime,
                makeShowErrors,
                isDisabled: disableForm,
              },
              {
                caption: `По умолчанию`,
                name: `defExecutionTime`,
                isHoursDisabled: onlyDate,
                isMinutesDisabled: onlyDate,
                error: showErrors && validate.defExecutionTime,
                makeShowErrors,
                isDisabled: disableForm,
              },
            ].map(WrappedTimeRange)}
          </Box>
          <Box loose pt="1md">
            <Switch
              label="Использовать в событии только дни"
              checked={onlyDate}
              disabled={disableForm}
              onChange={value => {
                changeFormValue(`onlyDate`, value)
                if (value) {
                  changeDate(
                    minExecutionTime,
                    maxExecutionTime,
                    defExecutionTime,
                  )
                }
                makeShowErrors(false)
              }}
            />
          </Box>
          <Box loose>
            <LinkContent
              haveLink={Boolean(externalLinkTemplate)}
              isDisabled={disableForm}
              changeFormValue={changeFormValue}
              getTestAttribute={getTestAttribute}
              extraError={showErrors && validate}
              extraChange={() => makeShowErrors(false)}
            />
          </Box>
        </Box>
        <Box
          loose
          flex
          row
          className={styles.EventTypeFormFooter}
          pt="12px"
          pb="12px"
        >
          <Box loose pl="2md">
            <PrimaryButton disabled={disableForm} type="submit">
              {isNew ? `Создать` : `Сохранить`}
            </PrimaryButton>
          </Box>
          <Box loose pl="2md">
            <FlatButton onClick={backToList}>Отмена</FlatButton>
          </Box>
          <Box loose />
        </Box>
      </form>
    </Box>
  )
}

const EventTypeReduxForm = reduxForm({
  onSubmit: (values, dispatch, { isNew, create, update, makeShowErrors }) => {
    const resourceVal = values.allowedResourceTypes
      ? values.allowedResourceTypes.map(item => item.value || item)
      : []
    const actionsData = {
      ...pick(values, [
        `color`,
        `code`,
        `defExecutionTime`,
        `externalLinkTemplate`,
        `maxExecutionTime`,
        `minExecutionTime`,
        `name`,
        `onlyDate`,
      ]),
      allowedResourceTypes: resourceVal,
    }
    if (isNew) {
      create(actionsData)
    } else {
      update(actionsData)
    }
    setTimeout(() => makeShowErrors(true), 1000)
  },
  form: FORM_NAME,
})(EventTypeForm)

const ConnectedEventTypeFilterForm = connect(
  (state, { isNew, eventType: { data } }) => ({
    initialValues: isNew
      ? {
          allowedResourceTypes: null,
          code: null,
          color: `#44bcf5`,
          contextUuid: null,
          defExecutionTime: null,
          externalLinkTemplate: null,
          id: null,
          maxExecutionTime: null,
          minExecutionTime: null,
          name: null,
          onlyDate: false,
        }
      : data,
    onlyDate: selector(state, `onlyDate`),
    allowedResourceTypes: selector(state, `allowedResourceTypes`),
    externalLinkTemplate: selector(state, `externalLinkTemplate`),
    minExecutionTime: selector(state, `minExecutionTime`),
    maxExecutionTime: selector(state, `maxExecutionTime`),
    defExecutionTime: selector(state, `defExecutionTime`),
    disableForm: isNew
      ? !hasPermission(EVENT_TYPE_CREATE)(state)
      : !hasPermission(EVENT_TYPE_UPDATE)(state),
  }),
  dispatch => ({
    changeFormValue: (field, value) =>
      dispatch(change(FORM_NAME, field, value)),
    changeOnlyDate: value => dispatch(change(FORM_NAME, `onlyDate`, value)),
    changeDate: (minExecutionTime, maxExecutionTime, defExecutionTime) => {
      const day = 86400
      const roundMinutesAndHours = time => Math.round(time / day) * day
      if (typeof minExecutionTime === `number`) {
        dispatch(
          change(
            FORM_NAME,
            `minExecutionTime`,
            roundMinutesAndHours(minExecutionTime),
          ),
        )
      }

      if (typeof maxExecutionTime === `number`) {
        dispatch(
          change(
            FORM_NAME,
            `maxExecutionTime`,
            roundMinutesAndHours(maxExecutionTime),
          ),
        )
      }

      if (typeof defExecutionTime === `number`) {
        dispatch(
          change(
            FORM_NAME,
            `defExecutionTime`,
            roundMinutesAndHours(defExecutionTime),
          ),
        )
      }
    },
  }),
)(EventTypeReduxForm)

export default ConnectedEventTypeFilterForm
